import dynamic from 'next/dynamic'
import { formatAmount } from '~/components/amount'
import { Button } from '~/components/button'
import { Box } from '~/components/layout'
import { useModal } from '~/components/modal'
import { Time } from '~/components/time'
import { Meta } from '~/components/typography'
import { routes } from '~/config/routes'
import { useToasts } from '~/context/toasts'
import { downloadPdf } from '~/utils/download-pdf'

import { CurrentUserTicketsQuery } from '../../graphql/current-user-tickets'
import { ButtonLink } from '../order/style'

const ModalQrCodes = dynamic(() => import('./components/modal-qr-codes'))

type TicketOrder = CurrentUserTicketsQuery['currentUserTickets'][0]

type Props = {
  order: TicketOrder
}

export const TicketOrder = ({ order }: Props) => {
  const modalQrCodes = useModal()
  const toasts = useToasts()

  const handleDownloadTickets = async () => {
    toasts.info('Downloading tickets...')
    try {
      await downloadPdf(
        routes.downloadTicketsPdf(order.id),
        `tickets-${order.id}.pdf`,
        'donor-portal',
      )
      toasts.success('Tickets downloaded')
    } catch {
      toasts.warning('Failed to download tickets')
    }
  }

  return (
    <>
      <div className="overflow-hidden border border-whiteFog rounded-sm items-center">
        <div className="border-b border-whiteFog">
          <div className="flex justify-between items-start gap-2 px-4 md:px-6 lg:px-10 pt-4 lg:pt-6 pb-5">
            <Box>
              <span className="font-medium text-base sm:text-lg leading-4 md:leading-7">
                {order.page.title}
              </span>
              {order.page.startsAt && (
                <Meta regular>
                  <Time format="LLL">{order.page.startsAt}</Time>
                </Meta>
              )}
              {order.page.venueName && (
                <Meta regular>{order.page.venueName}</Meta>
              )}
            </Box>
            <Box style={{ textAlign: 'right' }}>
              <div className="text-xl md:text-2xl font-medium">
                {formatAmount(order.amount)}
              </div>
              <Meta regular>
                {order.ticketCount} {order.ticketCount === 1 ? 'ticket' : 'tickets'}
              </Meta>
              <Meta regular>
                Purchased <Time format="LL">{order.created}</Time>
              </Meta>
            </Box>
          </div>
        </div>

        <div className="flex p-2 justify-end items-center bg-soft gap-4 md:gap-10">
          <ButtonLink onClick={modalQrCodes.show}>QR Codes</ButtonLink>
          <ButtonLink onClick={handleDownloadTickets}>Download Tickets</ButtonLink>
          <Button size="small" href={routes.donorPortal.ticketOrder(order.id)}>
            Manage Tickets
          </Button>
        </div>
      </div>

      <ModalQrCodes
        tickets={order.tickets}
        {...modalQrCodes}
      />
    </>
  )
}
